//-- Copyright (c) 2021 TMT Insights Group, LLC
import { AllTableColumnsConfig } from '@/__configs/config_types';
import { formatBytes } from '@/_utils/utils';
import { IngestFile } from '@/redux/slices/slice_types';
import { Grid, Typography } from '@mui/material';
import {
  ActionableTableStatus,
  DateTimeDisplay,
  EllipsisTooltip,
  FileTypeDisplay,
  IngestManagementOverflowMenu,
  IngestManagementPreview,
  IngestManagementRowExpand,
  PolarisIcon
} from '../../../../exports/columnCommonComponents';

const columns: AllTableColumnsConfig<IngestFile> = {
  expandable: {
    label: '',
    isExpandable: true,
    render: (row: IngestFile, frozenRightWidth: number) => <IngestManagementRowExpand data={row} previewWidth={frozenRightWidth} />
  },
  hid: {
    label: 'ID',
    sortField: 'id',
    position: 'left',
    width: 100,
    render: (cell: IngestFile['hid']) => <strong style={{ color: 'var(--primary-main)' }}>{cell}</strong>
  },
  created_at: {
    label: 'Created',
    position: 'left',
    sortField: 'created_at',
    render: (cell: IngestFile['created_at']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  version_serialized__name: {
    label: 'Edit Class',
    minWidth: 300,
    disableSort: true
  },
  version_serialized__lineage_meta__series_name: {
    label: 'Series Name',
    minWidth: 300,
    disableSort: true
  },
  version_serialized__lineage_meta__season_sequence_order: {
    label: 'Season #',
    minWidth: 100,
    disableSort: true
  },
  version_serialized__lineage_meta__episode_name: {
    label: 'Episode Name',
    minWidth: 350,
    disableSort: true
  },
  component_deliverable_type: {
    label: 'Deliverable Type',
    minWidth: 200,
    disableSort: true
  },
  version_serialized__lineage_meta__supplemental_name: {
    label: 'Supplemental Name',
    minWidth: 250,
    disableSort: true
  },
  due_date: {
    label: 'Due Date',
    render: (cell: IngestFile['due_date']) => {
      return cell ? <DateTimeDisplay>{cell}</DateTimeDisplay> : '-';
    }
  },
  version_serialized__external_id: {
    label: 'External ID',
    minWidth: 200,
    disableSort: true
  },
  version_serialized__lineage_meta__compilation_name: {
    label: 'Compilation Name',
    minWidth: 300,
    disableSort: true
  },
  filename: {
    label: 'File Name',
    minWidth: 400,
    render: (cell: IngestFile['filename']) => {
      return cell ? (
        <Grid container alignItems="center">
          <PolarisIcon icon="file" height={20} style={{ marginRight: 5, opacity: 0.6 }} />
          <EllipsisTooltip text={cell} maxWidth={340} />
        </Grid>
      ) : (
        <Typography sx={{ opacity: 0.5, fontSize: '1rem', display: 'inline-flex', alignItems: 'center' }}>
          <PolarisIcon icon="info" height={15} /> <em style={{ marginLeft: '5px' }}>Awaiting Filename Information</em>
        </Typography>
      );
    }
  },
  file_type: {
    label: 'File Type',
    disableSort: true,
    minWidth: 150,
    render: (cell: IngestFile['file_type']) => <FileTypeDisplay type={cell} />
  },
  file_size: {
    label: 'File Size',
    disableSort: true,
    render: (cell: IngestFile['file_size']) => {
      return cell ? (
        <Grid container alignItems="center">
          <PolarisIcon icon="clarity:hard-disk-solid" height={20} style={{ marginRight: 5, opacity: 0.6 }} />
          {formatBytes(cell)}
        </Grid>
      ) : (
        '-'
      );
    }
  },
  version_id: {
    label: 'Version ID',
    defaultDisabled: true,
    disableSort: true,
    render: (cell: IngestFile['version_id']) => {
      return cell ? cell : '-';
    }
  },
  preview: {
    label: 'Preview',
    position: 'right',
    disableSort: true,
    width: 110,
    render: (_: undefined, row: IngestFile, expanded: boolean, previewDimensions: { width?: number; height?: number }) => {
      return (
        <IngestManagementPreview
          data={row}
          expanded={expanded}
          previewDimensions={{ width: previewDimensions?.width || 0, height: previewDimensions?.height || 0 }}
        />
      );
    }
  },
  stage: {
    label: 'Status/Stage',
    width: 220,
    render: (cell: string, row: IngestFile) => <ActionableTableStatus status={cell} row={row} />,
    position: 'right',
    disableSort: false
  },
  overflow: {
    label: '',
    position: 'right',
    width: 135,
    disableSort: true,
    render: (cell: IngestFile, row: IngestFile) => <IngestManagementOverflowMenu data={row} />
  }
};

export default columns;
